import React from "react";
import { useState } from "react";
import { useEffect } from "react";
import DrEditor from "./Editor";
import AddButton from "./AddButton";
import EditButton from "./EditButton";
import { useRef } from "react";
import SequenceableControls from "./SequenceableControls";
import { faCommentsDollar } from "@fortawesome/free-solid-svg-icons";
import { uniqueSort } from "jquery";

/**
 * COMPONENT
 * <StatefulAssessments
      api={api_base}
      assmts={syl_matrix_assmts}
      assmtTypes={assmt_type_options}
      assmtPoints={assmt_matrix_points}
      editMode={canEditContent}
      focusKey={key_in_focus}
      focus={this.focus}
      lastUpdateTime={this.state.updateTime}
      lastUpdateType={this.state.lastUpdateType}
      containerId={container_id}
      reqOptions={this.reqOptions}
      syllabusId={id}
      units={units}
  />
*/
const StatefulAssessments = (props) => {
  const _isMounted = useRef(false);
  const {
    api,
    assmtTypes,
    assmtPoints,
    lastUpdateType,
    lastUpdateTime,
    reqOptions,
    syllabusId,
  } = props;
  const [assmts, setAssmts] = useState(hashByType(props.assmts || []));
  const [units, setUnits] = useState(props.units || []);
  const [points, setPoints] = useState(assmtPoints);
  const [reload, setReload] = useState(false);
  const [unitsHash, setUnitsHash] = useState(hashById(units));
  const totalPoints = sumAssmtPoints(points);

  const resetUnits = (unitsArr) => {
    if (_isMounted.current) {
      setUnits(unitsArr || []);
      setUnitsHash(hashById(unitsArr || []));
    }
  };

  const reloadUnits = () => {
    apiCall(
      `${api}units?unit[syllabus_id]=${syllabusId}`,
      reqOptions("GET"),
      (res) => res.json().then(resetUnits)
    );
  };

  useEffect(function onMount() {
    _isMounted.current = true;
    return function unMount() {
      _isMounted.current = false;
    };
  }, []);

  useEffect(
    function assmtPropUpdate() {
      if (_isMounted.current && props.assmts)
        setAssmts(hashByType(props.assmts || []));
    },
    [props.assmts]
  );

  useEffect(
    function unitPropUpdate() {
      resetUnits(props.units);
    },
    [props.units]
  );

  useEffect(
    function pointsPropUpdate() {
      if (_isMounted.current && props.assmtPoints) setPoints(props.assmtPoints);
    },
    [props.assmtPoints]
  );

  useEffect(
    function checkUpdateType() {
      if (_isMounted.current) {
        if (lastUpdateType === "syl_matrix_assmt") {
          setReload(true);
        } else if (lastUpdateType === "unit") {
          debounce(reloadUnits, _isMounted, null, 5);
        }
      }
    },
    [lastUpdateTime]
  );

  useEffect(
    function reloadAssmts() {
      if (reload) {
        apiCall(
          `${api}syl_matrix_assmts?syl_matrix_assmt[syllabus_id]=${syllabusId}`,
          reqOptions("GET"),
          (res) =>
            res.json().then((data) => {
              if (_isMounted.current) {
                setAssmts(hashByType(data));
                setReload(false);
              }
            }) /** callback **/
        ); /** apiCall **/
      } /** if (reload) **/
    },
    [reload]
  );

  const assmtGroups = assmtTypes.map((type, ix) => (
    <React.Fragment key={`assmt-grp-frgmt-${type.id}`}>
      <AssmtGroup
        key={`assmt-grp-${type.id}`}
        {...props}
        assmts={assmts[type.id]}
        pointsArr={points}
        parentKey={`assmt-grp-${type.id}`}
        pointsIx={ix}
        type={type}
        units={units}
        unitsHash={unitsHash}
      />
    </React.Fragment>
  ));

  return (
    <div className="clearfix">
      <table className="table table-bordered float-right">
        <thead>
          <tr key="assmts-header-tr">
            <th>Assessment</th>
            <th>
              Point
              <br />
              Value
            </th>
          </tr>
        </thead>
        <tbody>{assmtGroups}</tbody>
      </table>
      <label className="float-right">
        <strong>Total Points:</strong> {totalPoints}
      </label>
    </div>
  );
};

/**
 * COMPONENT
 * Section for a single type of assessment.
 **/
const AssmtGroup = (props) => {
  try {
    /** Don't render group in readonly mode if there are not assessments **/
    if (!props.editMode && props.assmts == null) return null;

    const _isMounted = useRef(false);
    const { api, parentKey, reqOptions, syllabusId, type, unitsHash } = props;
    const url = `${api}syl_matrix_assmts`;
    const [reload, setReload] = useState(false);
    const [assmts, setAssmts] = useState(props.assmts || []);
    const [units, setUnits] = useState(props.units || []);
    const maxSequence = assmts.length;

    useEffect(function setMounted() {
      _isMounted.current = true;
      return function setUnmounted() {
        _isMounted.current = false;
      };
    }, []);

    useEffect(
      function assmtPropsUpdate() {
        if (_isMounted.current) setAssmts(props.assmts || []);
      },
      [props.assmts]
    );

    useEffect(
      function assmtPropsUpdate() {
        if (_isMounted.current) setUnits(props.units || []);
      },
      [props.units]
    );

    useEffect(
      function loadData() {
        if (reload) {
          const body = {
            syl_matrix_unit: {
              syllabus_id: syllabusId,
              type_lookup_id: type.id,
            },
          };
          apiCall(url, reqOptions("GET", body), (res) =>
            res.json().then((data) => {
              if (_isMounted.current) {
                setAssmts(data);
                setReload(false);
              }
            })
          );
        } /** only do something if reload is true **/
      },
      [reload]
    );

    const resetAssmtsCallback = (res) =>
      res.json().then(
        (data) => {
          if (_isMounted.current) {
            setAssmts(data);
          }
        } /** process data **/
      );

    const updateItemSequence = (opts, e) => {
      const { id, value } = opts;
      const body = {
        syl_matrix_assmt: {
          syllabus_id: syllabusId,
          sequence: value,
        },
      };
      apiCall(
        `${url}/${id}`,
        reqOptions("PATCH", body),
        resetAssmtsCallback
      ); /** apiCall() **/
    };

    const addItemOfType = (opts, e) => {
      const body = {
        syl_matrix_assmt: {
          syllabus_id: syllabusId,
          type_lookup_id: type.id,
          sequence: 1,
        },
      };
      apiCall(url, reqOptions("POST", body), resetAssmtsCallback);
    };

    const removeItem = (opts, e) => {
      if (confirm("Are you sure you want to remove this assessment?")) {
        const body = {
          syl_matrix_assmt: { syllabus_id: syllabusId, active: false },
        };
        apiCall(
          `${url}/${opts.id}`,
          reqOptions("PATCH", body),
          (res) =>
            res.json().then(
              (data) => {
                if (_isMounted.current) {
                  setAssmts(data);
                }
              } /** process data **/
            ) /** callback **/
        ); /** apiCall() **/
      } /** confirm **/
    };

    const assessments = assmts.map((a) => (
      <AssmtRow
        {...props}
        key={`${parentKey}-assmt-${a.id}`}
        removeItem={removeItem}
        assmt={a}
        maxSequence={maxSequence}
        updateItemSequence={updateItemSequence}
        // units={units}
      />
    ));

    return (
      <>
        <AssmtsHeader
          key={`${parentKey}-header`}
          {...props}
          handleAdd={addItemOfType}
        />
        {assessments}
      </>
    );
  } catch (e) {
    console.error(e);
  }
};

/**
 * COMPONENT
 * Header row for a group of assessments
 **/
const AssmtsHeader = (props) => {
  try {
    const _isMounted = useRef(false);
    const _localUpdate = useRef(false);
    const {
      api,
      editMode,
      focus,
      focusKey,
      handleAdd,
      parentKey,
      pointsArr,
      pointsIx,
      reqOptions,
      syllabusId,
      type,
    } = props;
    const [points, setPoints] = useState(pointsArr[pointsIx]);
    const key = `${parentKey}-header`;
    const inFocus = focusKey === key;

    useEffect(function setMounted() {
      _isMounted.current = true;
      return function setUnmounted() {
        _isMounted.current = false;
      };
    }, []);

    useEffect(
      function sendPointsUpdate() {
        if (_isMounted.current && _localUpdate.current) {
          const body = {
            syllabus: {
              syllabus_id: syllabusId,
              assmt_matrix_points_arr: pointsArr.join(","),
            },
          };
          apiCall(
            `${api}syllabi/${syllabusId}`,
            reqOptions("PATCH", body),
            (res) => {
              _localUpdate.current = false;
            }
          );
        }
      },
      [points]
    );

    /* received an updated points array from action cable */
    useEffect(
      function pointsPropUpdate() {
        _localUpdate.current = false;
        if (_isMounted.current) setPoints(props.pointsArr[pointsIx]);
      },
      [props.pointsArr[pointsIx]]
    );

    /** locally update the state value, points **/
    const changePoints = (e) => {
      if (_isMounted.current) {
        _localUpdate.current = true;
        pointsArr[pointsIx] = e.target.value;
        setPoints(e.target.value);
      }
    };

    const ptsElem = inFocus ? (
      <input
        type="number"
        id={`${key}input`}
        className="assmt_mtrx_points_arr"
        style={{ width: "4rem" }}
        value={points || 0}
        min="0"
        onChange={(e) => changePoints(e)}
      />
    ) : (
      points
    );

    return (
      <>
        <tr id={key} className="table-success" onClick={(e) => focus(key, e)}>
          <th>
            {type.name}
            {editMode ? (
              <button
                className="badge btn btn-primary badge-pill ml-2"
                onClick={handleAdd}
              >
                <strong>+</strong>
              </button>
            ) : null}
          </th>
          <td className="total-pnts d-flex align-items-center">
            {ptsElem}
            {editMode ? (
              <EditButton
                type="Cumulative points for assessment group."
                focus={props.focus}
                parentKey={key}
              />
            ) : (
              ""
            )}
          </td>
        </tr>
        <tr key={`${key}-examples-tr`}>
          <th className="table-secondary pt-0 pb-0" colSpan="2">
            Examples
          </th>
        </tr>
      </>
    );
  } catch (e) {
    console.error(e);
  }
};

/**
 * COMPONENT
 * List of assessments belonging to a certain type
 **/
const AssmtRow = (props) => {
  try {
    const _isMounted = useRef(false);
    const _localUpdate = useRef(false);
    const {
      api,
      assmt,
      containerId,
      focus,
      focusKey,
      ix,
      maxSequence,
      parentKey,
      reqOptions,
      syllabusId,
      updateItemSequence,
      unitsHash,
    } = props;
    const key = `${parentKey}-assmt-${assmt.id}`;
    const url = `${api}syl_matrix_assmts/${assmt.id}`;
    const [unit, setUnit] = useState(unitsHash[assmt.unit_id] || {});
    const [units, setUnits] = useState(props.units || []);
    const [description, setDescription] = useState(assmt.assmt_name);
    const [timer, setTimer] = useState(null);
    const inFocus = focusKey === key;

    useEffect(function setMounted() {
      _isMounted.current = true;
      return function setUnmounted() {
        _isMounted.current = false;
      };
    }, []);

    /**
     * UNIT LOGIC
     **/
    const changeUnit = (e) => {
      const unit_id = e.target.value;
      /** update unit in the local component **/
      if (_isMounted.current) setUnit(unitsHash[unit_id] || {});
      /** send change to the server **/
      const body = {
        syl_matrix_assmt: { syllabus_id: syllabusId, unit_id: unit_id },
      };
      apiCall(`${url}`, reqOptions("PATCH", body), null);
    };
    /** jsx constants **/
    const unitLabel = unit.sequence ? (
      <span className="pl-1 col-10" title={unit.name}>
        <strong>[Unit/Module {unit.sequence}]</strong>
      </span>
    ) : (
      ""
    );

    const unitElem = inFocus ? (
      <UnitsDropdown
        {...props}
        parentKey={key}
        unit={unit}
        units={units}
        handleChange={changeUnit}
      />
    ) : (
      unitLabel
    );

    /** Received broadcast changing the unit for this assessment **/
    useEffect(
      function unitChanged() {
        if (_isMounted.current) setUnit(unitsHash[assmt.unit_id] || {});
      },
      [assmt.unit_id]
    );

    /** If the list of units in the syllabus has been updated **/
    useEffect(
      function unitsPropChanged() {
        if (_isMounted.current) {
          setUnit(unitsHash[assmt.unit_id] || {});
          setUnits(props.units);
        }
      },
      [props.units]
    );

    /**
     * DESCRIPTION LOGIC
     **/
    const changeDescription = (opts, e) => {
      if (_isMounted.current) {
        _localUpdate.current = true;
        setDescription(e.target.value);
      }
    };

    useEffect(
      function sendDescription() {
        const body = {
          container_id: containerId,
          syl_matrix_assmt: {
            syllabus_id: syllabusId,
            assmt_name: description,
          },
        };
        if (_localUpdate.current) {
          setTimer(
            debounce(
              () =>
                apiCall(`${url}`, reqOptions("PATCH", body), (res) => {
                  _localUpdate.current = false;
                }),
              _isMounted,
              timer,
              3
            )
          );
        }
      },
      [description]
    );

    useEffect(
      function descPropChanged() {
        if (_isMounted.current) {
          _localUpdate.current = false;
          setDescription(assmt.assmt_name);
        }
      },
      [assmt.assmt_name]
    );

    return (
      <tr id={key} className="test-matrix-assessment align-items-top">
        <td
          className="d-flex flex-wrap justify-content-left align-items-top"
          onClick={(e) => focus(key, e)}
        >
          <span>{assmt.sequence}.</span>
          {unitElem}
          <span className="col-md-10 col-sm-8">
            <DrEditor
              updateType="syl_matrix_assmt"
              defaultText="Assessment description..."
              text={description}
              parentKey={key}
              handleChange={changeDescription}
              changeOpts={null}
              focusKey={focusKey}
              dataReady={true}
            />
          </span>
        </td>
        <td>
          <SequenceableControls
            {...props}
            item={assmt}
            parentKey={key}
            itemIndex={ix}
            handleChange={updateItemSequence}
            maxSequence={maxSequence}
            type="syl_matrix_assmt"
            plType="syl_matrix_assmts"
          />
        </td>
      </tr>
    );
  } catch (e) {
    console.error(e);
  }
};

/**
 * create the unit options for each assessment
 **/
const UnitsDropdown = (props) => {
  try {
    const { units, handleChange, parentKey, unit, reqOptions } = props;

    const unitOpts = [
      <option key={parentKey + "unitdefault"} value={0}>
        No Unit/Module
      </option>,
    ].concat(
      units.map((u, ix) => {
        return (
          <option key={parentKey + "unit" + ix} value={u.id}>
            Unit/Module {u.sequence}
          </option>
        );
      })
    );
    return (
      <span className="pl-1">
        <select
          className="btn btn-secondary mr-3"
          value={unit.id || 0}
          onChange={(e) => handleChange(e)}
        >
          {unitOpts}
        </select>
      </span>
    );
  } catch (e) {
    console.error(e);
  }
};

/**
 * Utility functions for transforming data and
 * sending API requests.
 **/

const hashByType = (assmts) =>
  assmts.reduce((acc, a) => {
    acc[a.type_lookup_id] ||= [];
    acc[a.type_lookup_id].push(a);
    return acc;
  }, {}) || {};

const hashById = (arr) => arr.reduce((h, v) => (h[v.id] = v) && h, {});

const sumAssmtPoints = function (arr) {
  return arr.reduce((acc, v) => acc + (parseInt(v) || 0), 0);
};

const debounce = (fn, _isMounted, timer, delayFactor) => {
  const delay = (delayFactor || 0) * 100;
  if (timer) clearTimeout(timer);
  if (_isMounted.current) {
    return setTimeout(() => {
      fn();
    }, delay);
  }
};

const apiCall = (url, reqOptions, next) => {
  try {
    fetch(url, reqOptions).then((res) => {
      if (res.status === 401) {
        // alert('Unauthorized. Either your session has expired or you are not authorized to perform this action. Please try logging out and logging in again.')
        // if (confirm('Unauthorized. Either your session has expired or you are not authorized to perform this action. Redirect to login page? \n(Please note: if your session has expired, none of the changes you make to this syllabus will be saved until you log in again.)')) {
        //   sylContainer.props.history.push('/log_in');
        // }
        alert(
          "Not authorized to update the syllabus. Please reload the page and try again."
        );
      } else if (res.status >= 400) {
        alert(
          "There was an issue updating the syllabus. Please reload the page and try again."
        );
      } else if (next) {
        next(res);
      }
    });
  } catch (err) {
    //To Do: handle error
  }
};

export default StatefulAssessments;
